/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import {
  type Campaign,
  type CustomerCampaign,
  type DiscountDefinition,
  fetchCustomerCampaignByLookupKey,
  isCoreSeatDiscount,
} from 'embercom/lib/billing/campaign';
import { BILLING_PERIODS } from 'embercom/lib/billing';
import moment from 'moment-timezone';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type CardlessConversionModalService from 'embercom/services/cardless-conversion-modal-service';
import type ReactTeammateAppIsland from 'embercom/services/react-teammate-app-island';

const FREE_SEATS_WINDOW_DAYS = 30;
export const FREE_SEATS_CAMPAIGN_LOOKUP_KEY = 'campaign_free_seats_offer';

interface FreeSeatsCampaignMetadata {
  automationRate: number;
  finIsLive: boolean;
  subscriptionPhase: 'in_trial' | 'within_free_seats_window' | 'past_free_seats_window';
  daysSinceSubscriptionStarted: number | null;
}

type CampaignLookupKey = 'campaign_lennys_podcast';
type CampaignTranslationKeys = {
  trialBannerText: string;
  trialBannerCtaLabel: string;
  checkoutsBannerText: string;
  upgradeModalBannerText: string;
};

const CAMPAIGN_TRANSLATION_KEYS: Record<CampaignLookupKey, CampaignTranslationKeys> = {
  campaign_lennys_podcast: {
    trialBannerText: 'components.banners.campaigns.campaign_lennys_podcast-text',
    trialBannerCtaLabel: 'components.banners.campaigns.campaign_lennys_podcast-cta-label',
    checkoutsBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-checkouts-banner-text',
    upgradeModalBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-upgrade-modal-banner-text',
  },
};

export default class CompellingOfferCampaignService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentSetupService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIsland;

  @tracked campaign: Campaign | null = null;

  init() {
    super.init();
    this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'open-cardless-conversion-modal',
      () => {
        this.cardlessConversionModalService.toggle();
      },
    );
  }

  @keepLatestTask
  *fetchCampaign(): TaskGenerator<void> {
    if (this.currentCampaign?.lookup_key) {
      this.campaign = yield fetchCustomerCampaignByLookupKey(
        this.appService.app.id,
        this.currentCampaign.lookup_key,
      );
    }
  }

  get customer() {
    return this.customerService.customer;
  }

  get currentCampaign(): CustomerCampaign | undefined {
    return this.customer?.currentCampaign;
  }

  coreSeatDiscountForPlan(planId: string): DiscountDefinition | undefined {
    return this.campaign?.discount_definitions?.find(
      (dd) => dd.plan_id.toString() === planId && isCoreSeatDiscount(dd),
    );
  }

  get isFreeSeatsCampaign(): boolean {
    return (
      this.currentCampaign?.lookup_key === FREE_SEATS_CAMPAIGN_LOOKUP_KEY &&
      this.isEligibleForCampaignExperience
    );
  }

  get isEligibleForCampaignExperience(): boolean {
    let { canUseCompellingOffer, isStandaloneApp } = this.appService.app;

    return Boolean(this.customer) && !isStandaloneApp && canUseCompellingOffer;
  }

  get isCurrentCampaignRedeemable(): boolean {
    let campaign = this.currentCampaign;
    if (!campaign || campaign.redeemed) {
      return false;
    }

    return !this.isExpired(campaign.redemption_expires_at);
  }

  private isExpired(date: Date | string | null | undefined): boolean {
    if (!date) {
      return false;
    }

    return new Date(date) < new Date();
  }

  private translateCampaignKey(
    key: keyof CampaignTranslationKeys,
    params?: Record<string, unknown>,
  ): string {
    let lookupKey = this.currentCampaign?.lookup_key;

    if (!lookupKey || !(lookupKey in CAMPAIGN_TRANSLATION_KEYS)) {
      return '';
    }

    let translationKey = CAMPAIGN_TRANSLATION_KEYS[lookupKey as CampaignLookupKey][key];
    return this.intl.t(translationKey, { htmlSafe: true, ...params }) as string;
  }

  get checkoutsBannerText(): string {
    return this.translateCampaignKey('checkoutsBannerText');
  }

  get trialBannerCtaLabel(): string {
    return this.translateCampaignKey('trialBannerCtaLabel');
  }

  get trialBannerText(): string {
    return this.translateCampaignKey('trialBannerText');
  }

  get upgradeModalBannerText(): string {
    let discountFinishesAt = this.currentCampaign?.discount_finishes_at;
    let hasExpiry = discountFinishesAt ? 'true' : 'false';
    let date = discountFinishesAt ? moment(discountFinishesAt).format('D MMM, YYYY') : '';
    return this.translateCampaignKey('upgradeModalBannerText', { hasExpiry, date });
  }

  /*
    Returns metadata for the free seats campaign, including the current
    subscription phase:
    - in_trial: app is in trial or expired trial
    - within_free_seats_window: app is within FREE_SEATS_WINDOW_DAYS of a paid subscription
    - past_free_seats_window: app is past FREE_SEATS_WINDOW_DAYS of a paid subscription
  */
  get freeSeatsCampaignMetadata(): FreeSeatsCampaignMetadata {
    // TODO: Wire in real automation rate from campaign metadata
    let automationRate = 0.435;
    let finIsLive = !!this.aiAgentSetupService.isFinLive;

    if (this.cardlessTrialService.inTrialOrExpiredTrial) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'in_trial',
        daysSinceSubscriptionStarted: null,
      };
    }

    let customer = this.customerService.customer;
    if (!customer) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysSinceSubscriptionStarted: null,
      };
    }

    let startedAt = customer.subscription?.startedAt;

    if (customer.hasActiveSubscription && startedAt) {
      let daysSinceSubscriptionStarted = moment().diff(startedAt, 'days');

      if (daysSinceSubscriptionStarted <= FREE_SEATS_WINDOW_DAYS) {
        return {
          automationRate,
          finIsLive,
          subscriptionPhase: 'within_free_seats_window',
          daysSinceSubscriptionStarted,
        };
      }

      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysSinceSubscriptionStarted,
      };
    }

    return {
      automationRate,
      finIsLive,
      subscriptionPhase: 'past_free_seats_window',
      daysSinceSubscriptionStarted: null,
    };
  }

  canViewGenericCampaignBanners(period: BILLING_PERIODS = BILLING_PERIODS.Monthly): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      this.isCurrentCampaignRedeemable &&
      period === BILLING_PERIODS.Monthly &&
      !this.isFreeSeatsCampaign
    );
  }

  canViewRedeemedCampaignBanners(): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      !!this.currentCampaign &&
      this.currentCampaign.redeemed &&
      !this.isExpired(this.currentCampaign.discount_finishes_at)
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferCampaignService: CompellingOfferCampaignService;
    'compelling-offer-campaign-service': CompellingOfferCampaignService;
  }
}
